import { Injectable } from '@angular/core';
import { LanguageUtils } from '../utils/language.utils';
import { environment } from '../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { IResponseCurrentUser } from './auth.interface';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  private currentUser: any; // Aquí se almacenará la información del usuario
  private currentAppliant: any; // Aquí se almacenará la información del solicitante

  constructor(private http: HttpClient) { }

  async loginUser(user: string, pass: string): Promise<boolean> {
    const myHeaders = new Headers();
    myHeaders.append("Content-Type", "application/json");

    const raw = JSON.stringify({
      "identifier": user,
      "password": pass
    });

    const response = await fetch(environment.apiAuthUrl + '/management/login?language=es-ES', {
      method: "POST",
      headers: myHeaders,
      body: raw,
      redirect: "follow"
    });
    if (response.ok) {
      const token = await response.text();
      sessionStorage.setItem('token', token);
      if (await this.setUser()) {
        this.getAppliant();
        return true;
      }
    }
    return false;
  }

  async logoutUser() {
    try {
      // DELETE, https://127.0.0.1:8001/zbe/logout?language=es-ES
      await fetch(environment.apiAuthUrl + '/logout?language=es-ES', {
        method: 'DELETE',
        headers: {
          'Authorization': 'Bearer ' + sessionStorage.getItem('token')
        }
      });
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    }
    this.currentUser = null;
    this.currentAppliant = null;
    sessionStorage.removeItem('currentUser');
    sessionStorage.removeItem('token');
    window.location.href = '/';
  }

  getCurrentUser<T = any >(): T | null {
    // Retorna el usuario actual
    if (this.currentUser || sessionStorage.getItem('currentUser') !== null) {
      this.currentUser = JSON.parse(sessionStorage.getItem('currentUser') || '');
      return this.currentUser as T;
    }
    return null;
  }

  getCurrentAppliant(): any {
    // Retorna el solicitante actual
    if (this.currentAppliant || sessionStorage.getItem('currentAppliant') !== null) {
      this.currentAppliant = JSON.parse(sessionStorage.getItem('currentAppliant') || '');
      return this.currentAppliant;
    }
    return null;
  }

  isAuthenticated(): boolean {
    // Retorna si el usuario está autenticado o no
    return this.getCurrentUser() !== null;
  }

  isLevel1(): boolean {
    // Retorna si el usuario es administrador o no
    return this.isAuthenticated() && (this.currentUser.rol === 'level1');
  }
  isLevel2(): boolean {
    // Retorna si el usuario es administrador o no
    return this.isAuthenticated() && (this.currentUser.rol === 'level2');
  }
  isAdmin(): boolean {
    // Retorna si el usuario es administrador o no
    return this.isAuthenticated() && (this.currentUser.rol === 'administrator');
  }
  isPrivate(): boolean {
    return this.isLevel1() || this.isLevel2() || this.isAdmin();
  }

  hasApplicant(): boolean {
    return this.getCurrentAppliant() !== null;
  }

  async initLoginPublic() {
    const redirectBack = environment.apiAuthUrl + '/api/aoc';
    const redirectFront = environment.redirectFront;
    try {
      // getenvironment.apiAuthUrl +  /login?language=es-ES
      const response = await fetch(environment.apiAuthUrl + '/login?language=' + LanguageUtils.getLanguage());
      if (response.ok) {
        const token = await response.text();
        sessionStorage.setItem('token', token);
        const urlToIframe = environment.identidadURL + '?lang=' + LanguageUtils.getLang() + '&scope=autenticacio_usuari&state=' + token + '_' + redirectFront + '&redirect_uri=' + redirectBack + '&response_type=code&client_id=tramits.atm_zbe.cat&approval_prompt=auto';
        console.log(urlToIframe);
        return urlToIframe;
      } else {
        console.error('Error al iniciar sesión');
      }
    } catch (error) {
      console.error('Error al obtener los datos:', error);
    }
    return '';
  }

  async initLoginPrivate() {
    const redirectBack = environment.apiAuthUrl + '/api/aoc';
    const redirectFront = environment.redirectFront + '-private';
    try {
      // getenvironment.apiAuthUrl +  /login?language=es-ES
      const response = await fetch(environment.apiAuthUrl + '/login?language=' + LanguageUtils.getLanguage());
      if (response.ok) {
        const token = await response.text();
        sessionStorage.setItem('token', token);
        const urlToIframe = environment.identidadURL + '?lang=' + LanguageUtils.getLang() + '&scope=autenticacio_usuari&state=' + token + '_' + redirectFront + '&redirect_uri=' + redirectBack + '&response_type=code&client_id=tramits.atm_zbe.cat&approval_prompt=auto';
        console.log(urlToIframe);
        return urlToIframe;
      } else {
        console.error('Error al iniciar sesión');
      }
    } catch (error) {
      console.error('Error al obtener los datos:', error);
    }
    return '';
  }

  async setUser(isPrivate: boolean = false): Promise<boolean> {
    try {
      const response = await fetch(environment.apiAuthUrl + '/user?language=' + LanguageUtils.getLanguage(), {
        method: 'GET',
        headers: {
          'Authorization': 'Bearer ' + sessionStorage.getItem('token')
        }
      });
      const user = await response.json() as IResponseCurrentUser;
      if (user.operator) {
        this.currentUser = {
          id: user.token.issued,
          documentNumber: user.aoc.user.identifier,
          username: user.aoc.user.name,
          name: user.operator.name,
          email: user.aoc.user.email,
          rol: isPrivate ? user.operator.exceptions_profile : 'public',
          municipalities: user.operator?.municipalities || []
        };
      } else {
        this.currentUser = {
          id: user.token.issued,
          documentNumber: user.aoc.user.identifier,
          username: user.aoc.user.name,
          name: user.aoc.user.name,
          email: user.aoc.user.email,
          rol: 'public',
          municipalities: []
        };
      }

      sessionStorage.setItem('currentUser', JSON.stringify(this.currentUser));
      this.getAppliant();
      return true;
    } catch (error) {
      console.error('Error al obtener los datos:', error);
      return false;
    }
  }

  async getUser(): Promise<any> {
    const response = await fetch(environment.apiAuthUrl + '/user?language=' + LanguageUtils.getLanguage(), {
      headers: {
        'Authorization': 'Bearer ' + sessionStorage.getItem('token')
      }
    });

    return await response.json();
  }

  async getAppliant(): Promise<void> {
    if (!this.currentUser || !this.currentUser.documentNumber) {
      console.error('El usuario actual no está definido o no tiene un número de documento.');
      return;
    }

    const url = `${environment.apiPublicUrl}/registration/applicant?applicant=${this.currentUser.documentNumber}&language=es-ES`;
    console.log('url:', url);

    try {
      const response = await this.http.get(url, {
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`
        }
      }).toPromise();

      this.currentAppliant = response;
      sessionStorage.setItem('currentAppliant', JSON.stringify(this.currentAppliant));
    } catch (error) {
      console.error('Error al obtener el solicitante:', error);
    }
  }
}
